import classNames from 'classnames'
import { LockOn } from 'components/icons/LockOn'
import { useStudentAssignmentCubit } from 'hooks/cubits/studentAssignment'
import { useDialogs } from 'hooks/dialogs'
import { observer } from 'mobx-react-lite'
import { MaterialsModal } from './MaterialsModal'
import { BreakoutTooltip } from 'components/design-system/BreakoutTooltip'
import { useMemo } from 'react'
import { useTranslationTyped } from 'i18n/i18n'
import { useLTIMode } from 'hooks/use-lti-mode'
import { getRoleIssueForRoomState } from '@breakoutlearning/firebase-repository/sources-of-truth/getRoleIssueForRoomState'
import { TriangleAlertIcon } from 'components/icons/TriangleAlert'
import { Trans } from 'react-i18next'
import { Spinner } from 'components/Spinner'
import { SlideDeckDescriptionMarkdown } from 'components/SlideDeckDescriptionMarkdown'
export const StudentAssignmentInfoLoading = () => {
  return (
    <div className="mb-5 mt-5 w-full flex-grow rounded-3xl bg-core-tertiary py-10 lg:mb-0 lg:mr-5 lg:mt-0 lg:h-full">
      <div className="h-full max-h-full w-full">
        <div className="flex h-full flex-col items-center justify-center overflow-auto">
          Loading...
        </div>
      </div>
    </div>
  )
}

export const StudentAssignmentInfo = observer(function StudentAssignmentInfo() {
  const cubit = useStudentAssignmentCubit()
  const libraryObject = cubit.libraryObject
  const { showDialog } = useDialogs()
  const { t, tt } = useTranslationTyped()
  const section = libraryObject.section
  const assignment = libraryObject.assignment
  const slideDeckData = libraryObject.slideDeck.data
  const lockMaterials = cubit.materialsLocked

  const { ltiMode } = useLTIMode()

  const showMaterials = useMemo(() => {
    // if materials are locked, we are only showing an icon, so always render lock icon
    if (lockMaterials) return true

    // show only if available materials to render
    return cubit.availableStudentMaterials.length > 0
  }, [cubit.availableStudentMaterials, lockMaterials])

  return (
    <div className="mb-10 mt-5 flex-grow rounded-3xl bg-core-tertiary md:h-full lg:mb-0 lg:mr-5 lg:mt-0">
      <div className="max-h-full md:h-full">
        <div className="items-left flex flex-col justify-between md:h-full md:overflow-auto">
          <div className="top mt-8 flex items-center px-8">
            <div data-testid="assignment-logo">
              {slideDeckData.slideDeckImageURL && (
                <img
                  src={slideDeckData.slideDeckImageURL}
                  alt=""
                  className="max-h-[50px] w-auto"
                />
              )}
            </div>
            <div className="mx-5 h-full w-[1px] bg-surface-dim" />
            <div className="flex-grow">
              <div
                data-testid="assignment-class-section"
                className="text-label-large"
              >
                {section.data.className} - {section.data.sectionName}
              </div>
              <div
                data-testid="assignment-instructor"
                className="text-label-small text-fixed-grey"
              >
                {section.instructor.fullName}
              </div>
            </div>
            {cubit.estimatedExperienceDurationInMinutes > 0 && (
              <div
                data-testid="assignment-duration"
                className="flex flex-col items-center rounded-xl border border-outline-light px-2 py-1"
              >
                <div className="text-label-large">
                  {cubit.estimatedExperienceDurationInMinutes}
                </div>
                <div className="text-label-small text-fixed-grey">
                  {t('student_assignment.mins')}
                </div>
              </div>
            )}
          </div>
          <h2 className="mb-1 mt-5 px-9 text-body-medium text-fixed-grey">
            {t('student_assignment.experience_description')}
          </h2>
          <div
            data-testid="assignment-description"
            className="description isolate mb-2 mr-6 mt-2 flex min-h-[200px] flex-grow flex-col gap-4 overflow-y-auto bg-core-tertiary px-9 pr-5 text-xs leading-5 text-core-on-tertiary md:min-h-32"
          >
            <SlideDeckDescriptionMarkdown>
              {slideDeckData.slideDeckDescription || ''}
            </SlideDeckDescriptionMarkdown>
            <RoleAssignmentInfo />
            <MinGroupSizeInfo />
          </div>
          <div className="bottom flex-grow-0 px-8 pb-8">
            <div className="flex min-h-[100px] flex-row gap-1 md:mt-2">
              <div
                className={classNames(
                  'flex w-full flex-1 items-center justify-center rounded-lg bg-surface',
                  {
                    hidden: ltiMode,
                  }
                )}
              >
                <div>
                  {assignment.assignedAt && (
                    <BreakoutTooltip
                      content={assignment.formattedAssignedAtWithTime}
                    >
                      <p
                        data-testid="assignment-available-from"
                        className="cursor-default text-body-medium text-fixed-grey"
                      >
                        {tt.student_assignment.available_from()}:{' '}
                        <strong className="text-label-medium text-on-surface">
                          {assignment.formattedAssignedAt}
                        </strong>
                      </p>
                    </BreakoutTooltip>
                  )}
                  <BreakoutTooltip
                    content={assignment.formattedDueDateWithTime}
                    enabled={!!assignment.formattedDueDate}
                  >
                    {assignment.formattedDueDate && (
                      <p
                        data-testid="assignment-due-date"
                        className="mt-1 cursor-default text-body-medium text-fixed-grey"
                      >
                        {tt.student_assignment.due_date()}:{' '}
                        <strong className="text-label-medium text-on-surface">
                          {assignment.formattedDueDate}
                        </strong>
                      </p>
                    )}
                  </BreakoutTooltip>
                  {assignment.assignmentOverdueAt && assignment.expiresAt && (
                    <BreakoutTooltip
                      content={assignment.formattedExpiresAtWithTime}
                    >
                      <p className="mt-1 cursor-default text-body-medium text-fixed-grey">
                        {tt.student_assignment.expires_at()}:{' '}
                        <strong className="text-label-medium text-on-surface">
                          {assignment.formattedExpiresAt}
                        </strong>
                      </p>
                    </BreakoutTooltip>
                  )}
                </div>
              </div>
              {showMaterials && (
                <div className="w-full flex-1 rounded-lg bg-surface">
                  <div
                    tabIndex={!lockMaterials ? 0 : -1}
                    role={!lockMaterials ? 'button' : undefined}
                    data-testid="show-materials-button"
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault()
                        if (lockMaterials) return
                        showDialog(
                          () => <div>{<MaterialsModal cubit={cubit} />}</div>,
                          { trigger: e.currentTarget }
                        )
                      }
                    }}
                    onClick={
                      lockMaterials
                        ? undefined
                        : (e) => {
                            showDialog(
                              () => (
                                <div>{<MaterialsModal cubit={cubit} />}</div>
                              ),
                              { trigger: e.currentTarget }
                            )
                          }
                    }
                    className={classNames(
                      'flex h-full flex-col items-center justify-center p-4',
                      {
                        'cursor-pointer': !lockMaterials,
                      }
                    )}
                  >
                    {lockMaterials && (
                      <LockOn
                        size={20}
                        data-testid="course-materials-locked"
                        className="mt-4 stroke-grey-text"
                        role="img"
                        aria-label={tt.student_assignment.locked()}
                      />
                    )}
                    <div
                      className={classNames('text-xs text-grey-text', {
                        'mt-4': !lockMaterials,
                        'mb-4': lockMaterials,
                      })}
                    >
                      {t('student_assignment.course_materials')}
                    </div>
                    {!lockMaterials && (
                      <strong className="mb-4 mt-3 cursor-pointer text-label-medium text-core-on-tertiary">
                        {t('student_assignment.view_details')} &gt;
                      </strong>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  )
})

const RoleAssignmentInfo = observer(function RoleAssignmentMessage() {
  const cubit = useStudentAssignmentCubit()
  const { tt } = useTranslationTyped()
  const hasRoles = cubit.roles.length > 0
  const hasRoom =
    cubit.roomStateId && cubit.roomState && cubit.roomState.isLoaded

  // validate current room state against role requirements (user count, role distribution, etc.)
  const roleIssue = (() => {
    if (!hasRoles || !hasRoom) return null
    const res = getRoleIssueForRoomState({
      roles: cubit.roles.models,
      roomState: cubit.roomState!,
    })
    if (res) return res.issue
    return res
  })()

  const roleIssueWarrantsWarning = (() => {
    if (!roleIssue) return false
    switch (roleIssue) {
      case 'belowMinUsers':
        // this will be resolved organically as users join
        return false
      case 'aboveMaxUsers':
        // needs instructor action -- trigger will not resolve
        return true
      case 'unassignedUsers': {
        // wait a bit if room was just updated - role assignment trigger likely running
        return false
      }
      case 'invalidRoles':
        // all roles assigned but invalid, needs instructor action trigger can not resolve
        return true
    }
  })()

  if (!hasRoles || !cubit.userIsInCurrentRoom) return null

  if (!hasRoom)
    return (
      <div className="flex w-full flex-col gap-1 text-body-medium font-light">
        <h6 className="text-on-surface-var">
          {tt.student_assignment.role_assignment()}
        </h6>
        <div className="rounded-lg border border-fixed-accent-color bg-fixed-eggshell px-3 py-2">
          {tt.student_assignment.join_to_see_role()}
        </div>
      </div>
    )

  const roles = cubit.roomState?.data.assignmentRoles ?? {}
  const assignedRole = cubit.user.uid in roles ? roles[cubit.user.uid] : null
  const resolvedRole = cubit.roles.models.find(
    (role) => role.id === assignedRole
  )

  // if roles are invalid, or we are above max viable room threshold for roles, display the error
  if (roleIssueWarrantsWarning)
    return (
      <div className="flex w-full flex-col gap-1 text-body-medium font-light">
        <h6 className="text-on-surface-var">
          {tt.student_assignment.role_assignment()}
        </h6>
        <div
          data-testid="role-invalid-config-message"
          className="flex flex-row items-center gap-2 rounded-lg border bg-core-error px-3 py-2 text-core-on-primary"
        >
          <TriangleAlertIcon size={14} />
          <span>
            {tt.student_assignment.group_invalid_role_configuration()}
          </span>
        </div>
      </div>
    )

  // at this point there are no issues that should prevent automatic role assignment
  // so if we do not have a role, show waiting state
  if (!assignedRole)
    return (
      <div className="flex w-full flex-col gap-1 text-body-medium font-light">
        <h6 className="text-on-surface-var">
          {tt.student_assignment.role_assignment()}
        </h6>
        <div className="flex flex-row items-center gap-2 rounded-lg border border-fixed-accent-color bg-fixed-eggshell px-3 py-2">
          <Spinner size={0.625} />
          <span>{tt.student_assignment.waiting_for_role()}</span>
        </div>
      </div>
    )

  // user is assigned a role but the models are behind, show nothing
  if (!resolvedRole) return null

  // user has a valid assigned role, display it
  return (
    <div className="flex w-full flex-col gap-1 text-body-medium font-light">
      <h6 className="text-on-surface-var">
        {tt.student_assignment.role_assignment()}
      </h6>
      <span data-testid="assigned-role-name">
        <Trans
          i18nKey="student_assignment.assigned_to_role_name"
          values={{ roleName: resolvedRole.data.roleName }}
          components={{ strong: <strong className="font-bold" /> }}
        />
      </span>
      <div className="rounded-lg border border-fixed-accent-color bg-fixed-eggshell px-3 py-2">
        {tt.student_assignment.review_role_course_materials()}
      </div>
    </div>
  )
})

const MinGroupSizeInfo = observer(function MinGroupSizeInfo() {
  const cubit = useStudentAssignmentCubit()
  const { tt } = useTranslationTyped()

  const hasRoom =
    cubit.roomStateId && cubit.roomState && cubit.roomState.isLoaded
  const hasAssignment = cubit.assignment && cubit.assignment.isLoaded

  if (!hasRoom || !hasAssignment) return null

  const minGroupSizeIssue = cubit.minGroupSizeIssueForRoomState

  if (!minGroupSizeIssue) return null

  return (
    <div
      data-testid="minimum-group-size-error"
      className="flex flex-row items-center gap-2 rounded-lg border bg-core-error px-3 py-2 text-body-medium text-core-on-primary"
    >
      <TriangleAlertIcon size={10} />
      <span>
        {tt.student_assignment.session_requires_minimum_members({
          count: minGroupSizeIssue.min,
        })}
      </span>
    </div>
  )
})
